/**
 * Many files in one run: column auto-mapping, per-file mapping, file-level rejection, and the
 * single entry point the wizard calls (`prepareManufactureImport`).
 *
 * The mapping is chosen ONCE, by header NAME (auto-matched from the first file's headers, then
 * adjustable), and resolved to column indexes per file — so a second file with the same headers
 * in another order still imports. A file that cannot honour the mapping is rejected as a whole and
 * reported with `rowNumber: null`; every other file proceeds:
 *  - FILE_UNREADABLE      the reader threw (value: its message)
 *  - FILE_DUPLICATE_NAME  a file name already in the run (№ and report lines are scoped by name)
 *  - FILE_COLUMNS_MISSING a mapped header is absent (value: the missing header names)
 *  - FILE_EMPTY           no data rows (header only, or nothing)
 *
 * Pure: reading the files is the screen's job; this receives what the reader produced.
 */

import { cellToString } from '../importCells'
import { type ImportSheet } from '../importFileParser'
import { groupManufactures } from './group'
import { parseManufactureRows } from './parseRows'
import { type ManufactureProductIndex } from './resolve'
import {
  type ManufactureDraft,
  type ManufactureImportColumnMapping,
  type ManufactureImportError,
  type ParsedManufactureRow,
} from './types'

export type ManufactureImportField = keyof ManufactureImportColumnMapping

/** Per field, the HEADER NAME it is mapped to (as written in the first file), or null. */
export type ManufactureHeaderMapping = Record<ManufactureImportField, string | null>

/** Field order = report order; aliases are matched case- and whitespace-insensitively. */
export const MANUFACTURE_IMPORT_FIELDS: ReadonlyArray<{
  field: ManufactureImportField
  required: boolean
  aliases: readonly string[]
}> = [
  { field: 'date', required: true, aliases: ['дата', 'date'] },
  {
    field: 'product',
    required: true,
    aliases: ['товар', 'продукт', 'артикул', 'штрихкод', 'product', 'sku', 'barcode'],
  },
  { field: 'quantity', required: true, aliases: ['количество', 'кол-во', 'quantity', 'qty'] },
  { field: 'batch', required: false, aliases: ['партия', 'номер партии', 'batch', 'lot'] },
  {
    field: 'manufactureNo',
    required: false,
    aliases: ['№ производства', 'номер производства', '№', 'production no'],
  },
  { field: 'ingredient', required: false, aliases: ['ингредиент', 'ingredient'] },
  {
    field: 'ingredientPerUnit',
    required: false,
    aliases: ['расход на 1 ед.', 'расход', 'per unit'],
  },
]

/** An ingredient column only means something with these two (parseRows needs both). */
const INGREDIENT_COMPANIONS: ManufactureImportField[] = ['manufactureNo', 'ingredientPerUnit']

/** Same normalisation as the sales import's auto-map (trim + lower case), plus inner whitespace. */
export const normalizeHeader = (header: unknown): string =>
  String(header ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()

const emptyHeaderMapping = (): ManufactureHeaderMapping => ({
  date: null,
  product: null,
  quantity: null,
  batch: null,
  manufactureNo: null,
  ingredient: null,
  ingredientPerUnit: null,
})

/** The header row of a sheet as text (empty for a sheet with no rows). */
export const sheetHeaders = (sheet: ImportSheet): string[] => (sheet.rows[0] ?? []).map(cellToString)

/**
 * Header names → field mapping. Headers are scanned left to right: the first header matching a
 * field wins, and a header already taken by one field is never given to another.
 */
export const autoMapManufactureColumns = (headers: string[]): ManufactureHeaderMapping => {
  const mapping = emptyHeaderMapping()
  for (const header of headers) {
    const normalized = normalizeHeader(header)
    if (!normalized) continue
    const match = MANUFACTURE_IMPORT_FIELDS.find(
      ({ field, aliases }) =>
        mapping[field] === null && aliases.some((alias) => normalizeHeader(alias) === normalized)
    )
    if (match) mapping[match.field] = header
  }
  return mapping
}

export type ManufactureMappingIssueCode =
  /** A required field (date, product, quantity) is not mapped. */
  | 'MAPPING_REQUIRED_MISSING'
  /** Ingredient is mapped but this companion field (№ производства / per-unit) is not. */
  | 'MAPPING_INGREDIENT_INCOMPLETE'
  /** This field is mapped to a header an earlier field already uses. */
  | 'MAPPING_COLUMN_REUSED'

export interface ManufactureMappingIssue {
  code: ManufactureMappingIssueCode
  field: ManufactureImportField
}

/** Problems with the chosen mapping, in field order. Empty = the mapping can be applied. */
export const validateManufactureMapping = (
  mapping: ManufactureHeaderMapping
): ManufactureMappingIssue[] => {
  const issues: ManufactureMappingIssue[] = []
  const used = new Set<string>()
  for (const { field, required } of MANUFACTURE_IMPORT_FIELDS) {
    const header = mapping[field]
    if (header === null) {
      if (required) issues.push({ code: 'MAPPING_REQUIRED_MISSING', field })
      else if (mapping.ingredient !== null && INGREDIENT_COMPANIONS.includes(field)) {
        issues.push({ code: 'MAPPING_INGREDIENT_INCOMPLETE', field })
      }
      continue
    }
    const normalized = normalizeHeader(header)
    if (used.has(normalized)) issues.push({ code: 'MAPPING_COLUMN_REUSED', field })
    used.add(normalized)
  }
  return issues
}

/** What the screen's reader produced for one file. */
export type ManufactureImportFileInput =
  | { fileName: string; sheet: ImportSheet }
  | { fileName: string; readError: unknown }

export interface AcceptedManufactureFile {
  fileName: string
  sheet: ImportSheet
  mapping: ManufactureImportColumnMapping
}

export interface MapManufactureFilesResult {
  accepted: AcceptedManufactureFile[]
  /** File-level errors (`rowNumber: null`), in file order. */
  errors: ManufactureImportError[]
}

const readErrorMessage = (error: unknown): string =>
  error && typeof error === 'object' && 'message' in error
    ? String((error as { message: unknown }).message)
    : String(error)

/** Apply one header-name mapping to every file; reject the files that cannot honour it. */
export const mapManufactureFiles = (
  files: ManufactureImportFileInput[],
  mapping: ManufactureHeaderMapping
): MapManufactureFilesResult => {
  const accepted: AcceptedManufactureFile[] = []
  const errors: ManufactureImportError[] = []
  const seenNames = new Set<string>()

  for (const file of files) {
    const reject = (code: ManufactureImportError['code'], value = ''): void => {
      errors.push({ ref: { fileName: file.fileName, rowNumber: null }, code, value })
    }
    if (seenNames.has(file.fileName)) {
      reject('FILE_DUPLICATE_NAME')
      continue
    }
    seenNames.add(file.fileName)

    if (!('sheet' in file)) {
      reject('FILE_UNREADABLE', readErrorMessage(file.readError))
      continue
    }
    const { sheet } = file
    if (sheet.rows.length === 0) {
      reject('FILE_EMPTY')
      continue
    }

    const headers = sheetHeaders(sheet).map(normalizeHeader)
    const columns: ManufactureImportColumnMapping = {
      date: null,
      product: null,
      quantity: null,
      batch: null,
      manufactureNo: null,
      ingredient: null,
      ingredientPerUnit: null,
    }
    const missing: string[] = []
    for (const { field } of MANUFACTURE_IMPORT_FIELDS) {
      const header = mapping[field]
      if (header === null) continue
      const columnIndex = headers.indexOf(normalizeHeader(header))
      if (columnIndex === -1) missing.push(header)
      columns[field] = columnIndex === -1 ? null : columnIndex
    }
    if (missing.length > 0) {
      reject('FILE_COLUMNS_MISSING', missing.join(', '))
      continue
    }
    if (sheet.rows.length < 2) {
      reject('FILE_EMPTY')
      continue
    }
    accepted.push({ fileName: file.fileName, sheet, mapping: columns })
  }

  return { accepted, errors }
}

export interface PrepareManufactureImportOptions {
  files: ManufactureImportFileInput[]
  mapping: ManufactureHeaderMapping
  /** `buildManufactureProductIndex(productsDict)`, built once per run. */
  index: ManufactureProductIndex
}

export interface PrepareManufactureImportResult {
  /** Non-empty ⇒ nothing was read; fix the mapping first. */
  mappingIssues: ManufactureMappingIssue[]
  drafts: ManufactureDraft[]
  /** File-level errors first (file order), then row errors ordered by file, then row. */
  errors: ManufactureImportError[]
  acceptedFiles: string[]
  rejectedFiles: string[]
}

/** files → per-file mapping → rows → manufactures, across every accepted file. */
export const prepareManufactureImport = ({
  files,
  mapping,
  index,
}: PrepareManufactureImportOptions): PrepareManufactureImportResult => {
  const mappingIssues = validateManufactureMapping(mapping)
  if (mappingIssues.length > 0) {
    return { mappingIssues, drafts: [], errors: [], acceptedFiles: [], rejectedFiles: [] }
  }

  const { accepted, errors: fileErrors } = mapManufactureFiles(files, mapping)
  const rows: ParsedManufactureRow[] = []
  const rowErrors: ManufactureImportError[] = []
  for (const file of accepted) {
    const parsed = parseManufactureRows(file.sheet, file.mapping, file.fileName)
    rows.push(...parsed.rows)
    rowErrors.push(...parsed.errors)
  }
  // Group across all files at once: group keys are scoped by file name, so № 1 in two files
  // stays two manufactures.
  const grouped = groupManufactures(rows, index)
  rowErrors.push(...grouped.errors)

  const fileOrder = new Map(accepted.map((file, position) => [file.fileName, position]))
  const rowNumberOf = (error: ManufactureImportError): number => error.ref.rowNumber ?? 0
  rowErrors.sort(
    (a, b) =>
      (fileOrder.get(a.ref.fileName) ?? 0) - (fileOrder.get(b.ref.fileName) ?? 0) ||
      rowNumberOf(a) - rowNumberOf(b)
  )

  const acceptedFiles = accepted.map((file) => file.fileName)
  return {
    mappingIssues,
    drafts: grouped.drafts,
    errors: [...fileErrors, ...rowErrors],
    acceptedFiles,
    rejectedFiles: [...new Set(fileErrors.map((error) => error.ref.fileName))],
  }
}
